'use client';

import type { AnyObject } from '@sola/schema/dotcom/helpers.ts';
import { getAWSSignedRequestHeader } from '@sola/shared-utils/httpClient.ts';

import { experimentCookieKey } from '../experiments.ts';
import type { OptimizelyEventKey } from '../optimizely-schemas.ts';

declare global {
  var dataLayer: AnyObject[];
}

export const trackEvent = (event: AnyObject) => {
  if (!globalThis) {
    return;
  }
  if (!globalThis.dataLayer) {
    globalThis.dataLayer = [];
  }
  globalThis.dataLayer.push(event);
};

interface OptimizelyEventPayload {
  eventKey: OptimizelyEventKey;
  properties: Record<string, string>;
  revenue?: number;
  value?: number;
  url?: string;
}

/**
 * POSTs the conversion event to the app's own API route.
 *
 * The `x-amz-content-sha256` body hash is required, not optional. Skippy's
 * Next server sits behind CloudFront OAC, which signs origin requests to the
 * Lambda function URL with SigV4, and Lambda rejects unsigned payloads. Without
 * the hash, CloudFront rejects the request with a 403 before the route handler
 * runs — and on www the router worker then treats that 403 as "Skippy can't
 * serve this", retries against the legacy site, and returns legacy's HTML 404.
 *
 * The hash must cover the exact bytes sent, so `body` and the hashed object
 * are derived from the same `payload`.
 *
 * Equivalent to `signRequest: true` on the shared http client — see
 * `libs/skippy-sdk/src/requests.ts`. Kept as a raw fetch here for `keepalive`.
 */
const postOptimizelyEvent = async (payload: OptimizelyEventPayload) => {
  const body = JSON.stringify(payload);
  const signedHeader = await getAWSSignedRequestHeader(payload);

  // Dispatch the request after signing, then let keepalive carry it through any
  // navigation. Do not await the response: callers only need to wait until the
  // request is in flight, not add network latency to the user flow.
  fetch('/api/optimizely/events/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...signedHeader },
    body,
    keepalive: true,
  })
    .then((response) => {
      if (!response.ok) {
        console.warn(
          `[Optimizely] tracking failed: ${response.status} ${response.statusText}`,
          payload.eventKey,
        );
      }
    })
    .catch(() => {
      // Silent failure — don't block the user experience for tracking.
    });
};

const dispatchOptimizelyEvent = async (payload: OptimizelyEventPayload) => {
  // Skip if no Optimizely user identity — the server will reject without it
  // and there's nothing to attribute the event to.
  if (!document.cookie.includes(experimentCookieKey)) {
    return;
  }

  await postOptimizelyEvent(payload);
};

/**
 * Fire a generic Optimizely conversion event.
 * Supports custom event keys, properties, revenue, and value tags.
 */
export const trackOptimizelyEvent = (payload: OptimizelyEventPayload) => {
  return dispatchOptimizelyEvent(payload).catch(() => {
    // Never let tracking break the user flow.
  });
};
